import {
  Alert,
  Comments,
  DateSelector,
  Flex,
  Icons,
  Modal,
  Spinner,
  Subheading,
  TicketDetails,
  View,
} from '@tabeo/scarf'
import {
  maxLength,
  minLength,
  required,
} from '@tabeo/sharpei/utils/validations'
import InteractiveField from 'components/InteractiveField'
import PropTypes from 'prop-types'
import React, { useRef } from 'react'
import { Form, FormSpy } from 'react-final-form'
import { useAuth } from 'resources/Auth'
import { useComments } from 'resources/Comments'
import { useTicket } from 'resources/Ticket'
import { forcedValuesByType } from 'resources/Ticket/types'
import CloseConfirmation from './CloseConfirmation'
import AssigneeSelector from './Form/AssigneeSelector'
import EntitySelector from './Form/EntitySelector'
import FileUpload from './Form/FileUpload'
import Title from './Form/Title'
import TypeSelector from './Form/TypeSelector'
import Sidebar from './Sidebar'
import States from './States'
import TicketError from './TicketError'
import validate from './validate'

const { Description, Main } = TicketDetails

function TicketModal({ innerRef, onClose, ticketId }) {
  const {
    data,
    resource: { update, invalidateCache },
  } = useTicket()
  const {
    data: commentsData,
    resource: { create: createComment, remove: removeComment },
  } = useComments({
    endpoint: `/tickets/${ticketId}/comments`,
  })
  const {
    data: { user },
  } = useAuth()
  const preForcedValue = useRef({})
  const prevActive = useRef()

  const closeConfirmationRef = useRef()
  const hasUnsavedChanges = useRef(false)

  const handleChange = ({ active, values, valid }) => {
    // Blur
    if (active || !valid) {
      hasUnsavedChanges.current = true
    } else if (hasUnsavedChanges.current) {
      hasUnsavedChanges.current = false
    }
    if (prevActive.current && prevActive.current !== active && valid) {
      // Additional logic to decide submission
      update({ ...values, type: values.type || 'default' })
    }

    if (prevActive.current !== active) {
      prevActive.current = active
    }
  }

  return (
    <Modal
      ref={innerRef}
      onClose={onClose}
      containerProps={{
        py: [0, undefined],
        mb: [0, undefined],
      }}
      isOpenByDefault
      closable={() => !hasUnsavedChanges.current && !data?.localError}
    >
      {({ isOpen, close }) => {
        const handleClose = () => {
          close()
          // Drop the optimistic value to have a fresh fetch at the next open
          if (data?.localUpdateError) {
            invalidateCache()
          }
        }

        return isOpen ? (
          !data ? (
            <Spinner icon={{ fill: 'white' }} minHeight={['100vh', 'unset']} />
          ) : (
            <View
              minHeight={['100vh', 'unset']}
              maxWidth={['100%', '820px']}
              mx="auto"
              my={[0, 6]}
              bg="white"
              position="relative"
              borderRadius={[0, 1]}
              boxShadow={[0, 1]}
              py={[5, 10]}
              pl={[3, 8]}
              pr={[3, 10]}
            >
              <Form
                key={data?.id}
                initialValues={{
                  owners: [],
                  uploads: [],
                  ...data,
                }}
                validate={validate}
                onSubmit={() => {}}
                mutators={{
                  setFieldValue: (args, state, tools) => {
                    const [field, value] = args
                    tools.changeValue(state, field, () => value)
                  },
                }}
                keepDirtyOnReinitialize
              >
                {({
                  handleSubmit,
                  values,
                  errors,
                  form: {
                    mutators: { setFieldValue },
                  },
                }) => (
                  <form onSubmit={handleSubmit}>
                    <Icons.X
                      onClick={() => {
                        if (
                          hasUnsavedChanges.current ||
                          data?.localUpdateError
                        ) {
                          closeConfirmationRef.current.open()
                        } else {
                          handleClose()
                        }
                      }}
                      position="absolute"
                      fill="ink.1"
                      top={[5, 10]}
                      right={[5, 10]}
                      cursor="pointer"
                      data-testid="close-ticket-button"
                    />
                    <CloseConfirmation
                      innerRef={closeConfirmationRef}
                      onConfirm={() => {
                        handleClose()
                      }}
                    />

                    <TicketDetails>
                      <View
                        display="flex"
                        flexDirection={['column', 'row']}
                        width="100%"
                      >
                        <Main>
                          <TicketError
                            localUpdateError={data?.localUpdateError}
                            mb={4}
                            mt={[8, 0]}
                            mx={2}
                          />
                          <States ticket={values} px={2} />
                          <InteractiveField
                            name="title"
                            component={Title}
                            validate={[required, minLength(3), maxLength(100)]}
                          />
                          <InteractiveField
                            name="description"
                            component={Description}
                          />
                          <View px={2}>
                            <Flex
                              flexWrap="wrap"
                              justifyContent="flex-start"
                              alignItems="flex-start"
                              mt={3}
                            >
                              <InteractiveField
                                label="Type"
                                name="type"
                                component={TypeSelector}
                                containerProps={{ mb: 0, mt: 3, mr: 3 }}
                              />
                              <InteractiveField
                                label="Assignees"
                                component={AssigneeSelector}
                                name="owners"
                                containerProps={{ mb: 0, mt: 3, mr: 3 }}
                              />
                              <InteractiveField
                                label="Due date"
                                name="overdueAt"
                                component={DateSelector}
                                containerProps={{ mb: 0, mt: 3 }}
                                hasTime
                              />
                            </Flex>
                            <InteractiveField
                              label="Documents"
                              name="uploads"
                              component={FileUpload}
                              containerProps={{ mt: 6 }}
                            />

                            <View mt={4} mb={7}>
                              {errors?.links?.map((error, i) => (
                                <Alert key={i} status="warning" mb={2}>
                                  <Alert.Icon />
                                  {error}
                                </Alert>
                              ))}
                            </View>

                            <InteractiveField
                              label="Links"
                              name="entities"
                              component={EntitySelector}
                              containerProps={{ mt: 4 }}
                            />

                            <Sidebar
                              display={['flex', 'none']}
                              closeModal={close}
                              ticket={values}
                            />

                            <View mt={8}>
                              <Subheading mb={2} color="ink.2" fontSize="10px">
                                Comments
                              </Subheading>
                              <Comments
                                user={user}
                                onSubmit={async comment => {
                                  await createComment(comment)
                                  hasUnsavedChanges.current = false
                                }}
                                onDelete={removeComment}
                                comments={commentsData || undefined}
                                validate={(v = '') => {
                                  if (v) {
                                    hasUnsavedChanges.current = true
                                  } else {
                                    hasUnsavedChanges.current = false
                                  }
                                  return v.length > 2
                                }}
                                data-testid="comments-section"
                              />
                            </View>
                          </View>
                        </Main>
                        <View
                          width="1px"
                          height="100%"
                          bg="sky.1"
                          ml={3}
                          mr={5}
                        />
                        <Sidebar
                          display={['none', 'flex']}
                          closeModal={close}
                          ticket={values}
                        />
                      </View>
                    </TicketDetails>
                    <FormSpy
                      subscription={{
                        values: true,
                      }}
                      onChange={({ values }) => {
                        const forcedValues = {
                          ...(forcedValuesByType[values.type] || {}),
                        }

                        Object.entries(forcedValues).forEach(
                          ([name, value]) => {
                            if (values[name] !== value) {
                              preForcedValue.current[name] = values[name]
                              setFieldValue(name, value)
                            }
                          }
                        )

                        if (!Object.keys(forcedValues).length) {
                          // if some values were forced, revert them
                          Object.entries(preForcedValue.current).forEach(
                            ([name, value]) => {
                              if (values[name] !== value) {
                                delete preForcedValue.current[name]
                                setFieldValue(name, value)
                              }
                            }
                          )
                        }
                      }}
                    />
                    <FormSpy
                      subscription={{
                        active: true,
                        values: true,
                        valid: true,
                      }}
                      onChange={handleChange}
                    />
                  </form>
                )}
              </Form>
            </View>
          )
        ) : null
      }}
    </Modal>
  )
}

TicketModal.propTypes = {
  onClose: PropTypes.func,
  innerRef: PropTypes.object.isRequired,
  ticketId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
}

export default TicketModal
